import { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllCustomers,
  getAllCustomersForRestaurant,
} from "store/actions/customerAction";
import {
  Button,
  Card,
  CardFooter,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
  Spinner,
} from "reactstrap";

import OnlyHeader from "components/Layout/OnlyHeader.js";
import TableComponent from "components/shared/Table";
import Pagination from "components/shared/Pagination";
import GenderDistributionModal from "components/Modals/GenderDistributionModal";
import RangeSlider from "components/shared/RangeSlider";
import DebouncedSearch from "components/shared/DebouncedSearch";
import usePermissions from "hooks/usePermissions";

const Customers = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { customerData, isLoading, customerRestData, allCustomers } = useSelector(
    (state) => state.customer
  );
  const { get: getAccess } = usePermissions();
  const [sliderValue, setSliderValue] = useState([0, 100]);
  const [debouncedSlider, setDebouncedSlider] = useState([0, 100]);
  const [gender, setGender] = useState("all");
  const [genderModal, setGenderModal] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [hitsPerPage, setHitsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(0);

const genderCounts = useMemo(() => {
  return allCustomers?.reduce(
    (acc, customer) => {
      const gender = customer.gender?.toLowerCase();

      if (gender === "male") acc.male += 1;
      else if (gender === "female") acc.female += 1;

      return acc;
    },
    { male: 0, female: 0 }
  );
}, [allCustomers]);


  const toggleGenderModal = () => setGenderModal((prev) => !prev);

  const handleSearch = useCallback((value) => {
    setDebouncedSearch(value);
  }, []);

  const handleSliderChange = (newValue) => {
    setSliderValue(newValue);
  };

  const handleGenderChange = (e) => {
    setGender(e.target.value);
    setCurrentPage(0);
  };

  const handleHitsPerPageChange = (e) => {
    setHitsPerPage(parseInt(e.target.value));
    setCurrentPage(0);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSlider(sliderValue);
    }, 2000);

    return () => clearTimeout(handler);
  }, [sliderValue]);

  useEffect(() => {
    if (user?.restaurantID) {
      dispatch(getAllCustomersForRestaurant(user.restaurantID));
    }
  }, [dispatch, user?.restaurantID]);

  useEffect(() => {
    dispatch(
      getAllCustomers(
        user?.restaurantID,
        debouncedSearch,
        currentPage,
        gender,
        debouncedSlider,
        hitsPerPage
      )
    );
  }, [
    debouncedSearch,
    hitsPerPage,
    gender,
    currentPage,
    debouncedSlider,
    dispatch,
    user?.restaurantID,
  ]);

  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearch, gender, hitsPerPage, sliderValue]);

  const renderCustomers = () => {
    if (!customerData?.length) {
      return (
        <tr>
          <td colSpan={4} className="text-center py-5">
            No Customer Found.
          </td>
        </tr>
      );
    }

    return customerData.map((customer) => (
      <tr key={customer.id || customer.email}>
        <th scope="row">{customer.name}</th>
        <td>{customer.age || "-"}</td>
        <td>{customer.gender || "-"}</td>
        <td>
          {customer?.email && customer?.email !== "--WAITER-CUSTOMER--"
            ? customer.email
            : customer?.contactNumber}
        </td>
      </tr>
    ));
  };

  return (
    <>
      <OnlyHeader />
      <Container className="mt--7 mb-5" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader>
                <Row className="align-items-center">
                  <Col sm="6">
                    <h3 className="py-2">Customers</h3>
                  </Col>
                  <Col sm="6" className="d-flex justify-content-end">
                    <Button
                      color="primary"
                      onClick={toggleGenderModal}
                      disabled={!customerData?.length}
                    >
                      Customer Demographics
                    </Button>
                  </Col>
                </Row>

                <Row>
                  <Col sm="6" lg="3" className="mt-3">
                    <DebouncedSearch
                      placeholder="Search customers..."
                      onSearch={handleSearch}
                    />
                  </Col>
                  <Col sm="6" lg="3" className="mt-3">
                    <Input
                      type="select"
                      value={gender}
                      onChange={handleGenderChange}
                    >
                      <option value="all">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </Input>
                  </Col>
                  <Col sm="6" lg="3" className="mt-3">
                    <Input
                      type="select"
                      value={hitsPerPage}
                      onChange={handleHitsPerPageChange}
                    >
                      <option value="5">Show Entries</option>
                      <option value="10">10</option>
                      <option value="15">15</option>
                      <option value="20">20</option>
                      <option value="50">50</option>
                    </Input>
                  </Col>
                  <Col sm="6" lg="3" className="mt-3 text-center">
                    <RangeSlider
                      value={sliderValue}
                      setValue={handleSliderChange}
                      setCurrentPage={setCurrentPage}
                      adminColor={user?.adminColor}
                    />
                    <span className="text-nowrap">Age Range</span>
                  </Col>
                </Row>
              </CardHeader>

              {isLoading ? (
                <div className="d-flex justify-content-center align-items-center py-5">
                  <Spinner size="md" color="primary" />
                </div>
              ) : !getAccess ? (
                <Row className="py-4 justify-content-center align-items-center">
                  You don't have permission to access this page.
                </Row>
              ) : (
                <TableComponent>
                  <thead className="thead-light">
                    <tr>
                      <th scope="col">Name</th>
                      <th scope="col">Age</th>
                      <th scope="col">Gender</th>
                      <th scope="col">Email/Phone</th>
                    </tr>
                  </thead>
                  <tbody>{renderCustomers()}</tbody>
                </TableComponent>
              )}

              <CardFooter className="py-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={customerRestData?.nbPages}
                  onPageChange={setCurrentPage}
                />
              </CardFooter>
            </Card>
          </div>
        </Row>
      </Container>

      <GenderDistributionModal
        genderModal={genderModal}
        genderToggle={toggleGenderModal}
        genderCounts={genderCounts}
      />
    </>
  );
};

export default Customers;
